import { PATHS, UNSPECIFIED_LABEL } from '../config/constants';
import { BigBatch } from '@qualdesk/firestore-big-batch';
import firebase from 'firebase/app';
import {
  checkForUndefinedInObject,
  getLastUpdated,
  recordError,
} from '../shared/logTools';
import {
  fileNameAndExtensionFromPath,
  getBlobFile,
  getImportsSortedByRoom,
  getSafeTextFromText,
  getStorageForPath,
  isSameFloorplan,
  removeUndefinedFromObject,
  statuses,
} from '../shared/utilities';
import { setLoading, setSnackMessage } from './ui';

import { functions } from '../config/store';
import {
  MAX_ADDITIONAL_SURVEYORS,
  getSurveyStatusForAssignment,
  normaliseAdditionalSurveyorIds,
} from '../shared/surveyorAssignment';
import { FORM_CONFIG_DOC_ID } from './form-config';
import {
  FLOOR_PLANS_CONFIG_DOC_ID,
  floorPlansArrayToConfigMap,
  queueFloorPlansConfigDocWrite,
  resolveSurveyFloorPlansConfig,
  stripUnassignedFloorPlanMarkers,
} from './floorplans-config';
import { Statuses } from '../types/status';
import isEqual from 'lodash.isequal';
import {
  getActiveRoomScheduleCount,
  getRoomScheduleEntriesForSetup,
} from '../shared/roomScheduleSetup';

function sanitiseSurveyorIds({ surveyorIds, surveyorId }) {
  const mainSurveyorId = `${surveyorId || ''}`.trim();
  const dedupedSurveyorIds = normaliseAdditionalSurveyorIds(
    surveyorIds,
    mainSurveyorId,
  );

  return [
    mainSurveyorId && mainSurveyorId !== UNSPECIFIED_LABEL
      ? mainSurveyorId
      : null,
    ...dedupedSurveyorIds.slice(0, MAX_ADDITIONAL_SURVEYORS),
  ].filter(Boolean);
}

function getCanonicalSurveyorsMap({
  surveyorIds,
  existingSurveyorsMap,
  surveyorsById,
  mainSurveyorId,
  mainSurveyorName,
  preserveExistingStatus = true,
  defaultStatus = Statuses.ISSUED,
}) {
  const generatedLastUpdated = getLastUpdated().lastUpdated;
  return surveyorIds.reduce((acc, uid) => {
    const existingEntry = existingSurveyorsMap?.[uid];
    const surveyorData = surveyorsById?.[uid];
    const nextName =
      surveyorData && surveyorData.nameFirst && surveyorData.nameLast
        ? `${surveyorData.nameFirst} ${surveyorData.nameLast}`
        : uid === mainSurveyorId && mainSurveyorName
        ? mainSurveyorName
        : existingEntry?.name || uid;

    acc[uid] = removeUndefinedFromObject({
      name: nextName,
      status: preserveExistingStatus
        ? existingEntry?.status || defaultStatus
        : defaultStatus,
      lastUpdated:
        preserveExistingStatus && existingEntry?.status
          ? existingEntry?.lastUpdated || generatedLastUpdated
          : generatedLastUpdated,
    });

    return acc;
  }, {});
}

function getCanonicalSurveyorAssignment({
  selectedSurveyorIds,
  mainSurveyorId,
  mainSurveyorName,
  mainSurveyorEmail,
  existingSurveyorsMap,
  surveyorsById,
  preserveExistingStatus = true,
  defaultStatus = Statuses.ISSUED,
}) {
  const surveyorIds = sanitiseSurveyorIds({
    surveyorIds: selectedSurveyorIds || [],
    surveyorId: mainSurveyorId,
  });
  const surveyors = getCanonicalSurveyorsMap({
    surveyorIds,
    existingSurveyorsMap,
    surveyorsById,
    mainSurveyorId,
    mainSurveyorName,
    preserveExistingStatus,
    defaultStatus,
  });

  return {
    surveyorId: mainSurveyorId,
    surveyorName: mainSurveyorName,
    surveyorEmail: mainSurveyorEmail,
    surveyorIds,
    surveyors,
  };
}

function getRoomScheduleSnapshotEntry({ room, deleted }) {
  return {
    id: room.id,
    name: String(room.name || '').trim(),
    floor: String(room.floor || '').trim(),
    areaFloor: Number(room.areaFloor) || 0,
    areaWall: Number(room.areaWall) || 0,
    notes: String(room.notes || '').trim(),
    imported: !!room.imported,
    deleted: deleted || 0,
  };
}

function getRoomDocForSetup({
  roomScheduleEntry,
  existingRoomData,
  surveyData,
  lastUpdated,
}) {
  const roomSnapshotEntry = getRoomScheduleSnapshotEntry({
    room: roomScheduleEntry,
    deleted: roomScheduleEntry.deleted || 0,
  });

  return removeUndefinedFromObject({
    ...existingRoomData,
    ...roomSnapshotEntry,
    id: roomSnapshotEntry.id,
    clientId: surveyData.clientId,
    siteId: surveyData.siteId,
    siteName: surveyData.siteName ?? existingRoomData.siteName,
    buildingId: surveyData.buildingId,
    buildingName: surveyData.buildingName ?? existingRoomData.buildingName,
    surveyId: surveyData.id,
    surveyName: surveyData.name ?? existingRoomData.surveyName,
    status: existingRoomData?.status || Statuses.NOT_STARTED,
    surveyorStatus: existingRoomData?.surveyorStatus || {},
    created: existingRoomData?.created || Date.now(),
    deleted: roomSnapshotEntry.deleted || 0,
    assetsNumCreated: existingRoomData?.assetsNumCreated || 0,
    assetsNumDeleted: existingRoomData?.assetsNumDeleted || 0,
    assetsNumFlagged: existingRoomData?.assetsNumFlagged || 0,
    partialsNumCreated: existingRoomData?.partialsNumCreated || 0,
    partialsNumDeleted: existingRoomData?.partialsNumDeleted || 0,
    images: existingRoomData?.images || [],
    images360: existingRoomData?.images360 || [],
    lastUpdated,
  });
}

async function uploadFloorplan(ref, fileUrl, floor, fileName) {
  try {
    const file = await getBlobFile(fileUrl);
    await ref.put(file);
    const url = await ref.getDownloadURL();
    return { url, floor, fileName };
  } catch (error) {
    throw new Error(
      `There has been an error uploading your floor plan file: ${fileName}. Please go back to the floor plan step and add it again (without clicking remove), then try to save your survey.`,
      { cause: { type: 'FloorPlanUpload', originalError: error } },
    );
  }
}

function getLegacyVersionMigrationPatch(surveyData) {
  return {
    ...(typeof surveyData?.formFieldsVersion === 'number'
      ? {}
      : { formFieldsVersion: 1 }),
    ...(typeof surveyData?.floorPlansVersion === 'number'
      ? {}
      : { floorPlansVersion: 1 }),
  };
}

function getSurveyConfigDocRefs(surveyDocRef) {
  return {
    formConfigDocRef: surveyDocRef.collection('config').doc(FORM_CONFIG_DOC_ID),
    floorPlansConfigDocRef: surveyDocRef
      .collection('config')
      .doc(FLOOR_PLANS_CONFIG_DOC_ID),
  };
}

function queueSurveyConfigDocWrites({
  batch,
  formConfigDocRef,
  floorPlansConfigDocRef,
  formFields,
  formOptions,
  floorPlansByFloor,
}) {
  const lastUpdated = getLastUpdated();
  batch.set(
    formConfigDocRef,
    {
      formFields,
      formOptions,
      ...lastUpdated,
    },
    { merge: true },
  );
  queueFloorPlansConfigDocWrite({
    batch,
    floorPlansConfigDocRef,
    floorPlansByFloor,
    lastUpdated,
  });
}

async function queueRoomDocSetupWrites({
  batch,
  roomsCollectionRef,
  roomScheduleEntries,
  surveyData,
  lastUpdated,
}) {
  const existingRoomDocs = await roomsCollectionRef.get();
  const existingRoomsById = new Map(
    existingRoomDocs.docs.map((doc) => [doc.id, doc]),
  );
  const roomScheduleDataById = new Map();

  roomScheduleEntries.forEach((roomEntry) => {
    roomScheduleDataById.set(roomEntry.id, roomEntry);
    const roomDocRef = roomsCollectionRef.doc(roomEntry.id);
    const existingRoomData = existingRoomsById.get(roomEntry.id)?.data() || {};
    batch.set(
      roomDocRef,
      getRoomDocForSetup({
        roomScheduleEntry: roomEntry,
        existingRoomData,
        surveyData,
        lastUpdated,
      }),
      { merge: true },
    );
  });

  return { existingRoomDocs, roomScheduleDataById };
}

function isNotDeleted(deleted) {
  return deleted == null || Number(deleted) === 0;
}

function getDeletedScheduleEntriesById(roomScheduleData = []) {
  return new Map(
    roomScheduleData
      .filter((room) => room?.id && !isNotDeleted(room.deleted))
      .map((room) => [room.id, room]),
  );
}

async function queueRoomAndContentsDeletion({
  batch,
  roomDoc,
  deletedAt,
  lastUpdated,
}) {
  const roomData = roomDoc.data();
  const roomWasActive = isNotDeleted(roomData.deleted);
  let deletedAssetsCount = 0;
  let deletedPartialsCount = 0;
  const roomAssetDocs = await roomDoc.ref.collection('assets').get();

  roomAssetDocs.forEach((assetDoc) => {
    const assetData = assetDoc.data();

    if (!isNotDeleted(assetData.deleted)) {
      return;
    }

    deletedAssetsCount += 1;
    let assetDeletedPartialsCount = 0;
    const updatedPartials = { ...(assetData.partials || {}) };

    Object.entries(assetData.partials || {}).forEach(([partialId, partial]) => {
      if (!isNotDeleted(partial?.deleted)) {
        return;
      }

      assetDeletedPartialsCount += 1;
      deletedPartialsCount += 1;
      updatedPartials[partial.id || partialId] = {
        ...partial,
        deleted: deletedAt,
      };
    });

    batch.update(assetDoc.ref, {
      deleted: deletedAt,
      partials: updatedPartials,
      partialsNumDeleted:
        (assetData.partialsNumDeleted || 0) + assetDeletedPartialsCount,
      lastUpdated,
    });
  });

  batch.update(roomDoc.ref, {
    deleted: roomWasActive ? deletedAt : roomData.deleted,
    assetsNumDeleted: (roomData.assetsNumDeleted || 0) + deletedAssetsCount,
    partialsNumDeleted:
      (roomData.partialsNumDeleted || 0) + deletedPartialsCount,
    lastUpdated,
  });

  return {
    rooms: roomWasActive ? 1 : 0,
    assets: deletedAssetsCount,
    partials: deletedPartialsCount,
  };
}

export const onSurveySubmitted =
  ({ surveyId }) =>
  async (dispatch, getState, { getFirebase }) => {
    // initialise these as empty
    let surveyUpdateObj = { roomSchedule: null, floorPlans: [] };
    const { files, surveyForm } = getState();

    try {
      const firestore = getFirebase().firestore();
      const { client, survey: currentSurveyData } = getState().firestore.data;
      const { id: clientId, name: clientName } = client;

      const {
        name,
        siteId,
        siteName,
        gifa,
        external,
        surveyorId,
        surveyorName,
        surveyorEmail,
        surveyType,
        estimatedStartDate,
        estimatedDuration,
        buildingId,
        buildingName,
        assetListId,
        roomSchedule,
        floorPlans,
        formFields,
        formOptions,
        importData,
        uploadsNotFound,
        uploadsOnOtherDevice,
        uploadsQueueSize,
        rules,
        surveyTags,
        surveyorIds: selectedSurveyorIds,
      } = surveyForm;

      const surveyors = getState().firestore.ordered?.surveyors || [];
      const surveyorsById = surveyors.reduce((acc, surveyor) => {
        if (surveyor?.uid) {
          acc[surveyor.uid] = surveyor;
        }
        return acc;
      }, {});
      const formOptionsForWrite =
        formOptions || currentSurveyData?.formOptions || {};
      const existingSurveyorsMap = currentSurveyData?.surveyors || {};
      const canonicalSurveyorAssignment = getCanonicalSurveyorAssignment({
        selectedSurveyorIds: selectedSurveyorIds || [],
        mainSurveyorId: surveyorId,
        mainSurveyorName: surveyorName,
        mainSurveyorEmail: surveyorEmail,
        existingSurveyorsMap,
        surveyorsById,
      });

      const formFieldsParsed = formFields.map((field) => {
        return {
          ...field,
          // only assign id based on label if its a user created field
          // this allows the user to rename the base fields but not affect the id
          // have to fall back to using the label if the id doesn't already exist (legacy reasons)
          id: field.base
            ? field.id || getSafeTextFromText(field.label)
            : getSafeTextFromText(field.label),
          // have to cater for copying old formfields where hidden/base didn't exist
          hidden: typeof field.hidden !== 'undefined' ? field.hidden : false,
          base: typeof field.base !== 'undefined' ? field.base : false,
        };
      });

      surveyUpdateObj = {
        status:
          surveyorId === UNSPECIFIED_LABEL
            ? statuses.UNASSIGNED
            : statuses.ISSUED,
        name,
        deleted: 0,
        synced: 0,
        clientId,
        clientName,
        siteId,
        siteName,
        gifa,
        external,
        surveyorId,
        surveyorName,
        surveyorEmail,
        surveyType,
        estimatedStartDate,
        estimatedDuration,
        buildingId,
        buildingName,
        assetListId,
        roomsNumNotStarted: getActiveRoomScheduleCount(roomSchedule?.data),
        roomsNumInProgress: 0,
        roomsNumCompleted: 0,
        roomsNumDeleted: 0,
        assetsNumCreated: 0,
        assetsNumDeleted: 0,
        assetsNumImported: 0,
        partialsNumCreated: 0,
        partialsNumDeleted: 0,
        assetsNumFlagged: 0,
        formFieldsNum: formFieldsParsed.length,
        formFields: formFieldsParsed,
        formOptions: formOptionsForWrite,
        roomSchedule: null,
        floorPlans: [],
        uploadsNotFound: uploadsNotFound || 0,
        uploadsOnOtherDevice: uploadsOnOtherDevice || 0,
        uploadsQueueSize: uploadsQueueSize || 0,
        rules: rules ?? null,
        surveyTags: surveyTags || [],
        ...canonicalSurveyorAssignment,
      };
      Object.assign(
        surveyUpdateObj,
        getLegacyVersionMigrationPatch(currentSurveyData),
      );
      const floorPlanStorage = getStorageForPath(
        PATHS.STORAGE.IMAGE_FLOOR_PLAN,
      );
      const batch = new BigBatch({ firestore });

      const clientDocRef = await firestore.collection('clients').doc(clientId);

      let surveyDocRef;

      if (surveyId) {
        surveyDocRef = await clientDocRef.collection('surveys').doc(surveyId);
        surveyUpdateObj = {
          ...surveyUpdateObj,
          assetsNumCreated: currentSurveyData.assetsNumCreated,
          assetsNumDeleted: currentSurveyData.assetsNumDeleted || 0,
          assetsNumImported: currentSurveyData.assetsNumImported || 0,
          partialsNumCreated: currentSurveyData.partialsNumCreated || 0,
          partialsNumDeleted: currentSurveyData.partialsNumDeleted || 0,
          roomsNumDeleted: currentSurveyData.roomsNumDeleted || 0,
        };
      } else {
        // assign the created date
        surveyDocRef = await clientDocRef.collection('surveys').doc();
        surveyUpdateObj = {
          ...surveyUpdateObj,
          created: Date.now(),
          id: surveyDocRef.id,
          duplicatedSurveyId: surveyId || null,
        };
      }

      const { id } = surveyDocRef;
      const { formConfigDocRef, floorPlansConfigDocRef } =
        getSurveyConfigDocRefs(surveyDocRef);
      const existingFormConfigSnapshot = await formConfigDocRef.get();
      const existingFormConfig = existingFormConfigSnapshot.exists
        ? existingFormConfigSnapshot.data() || {}
        : {};
      const existingFloorPlansConfigSnapshot =
        await floorPlansConfigDocRef.get();
      const existingFloorPlansConfig = existingFloorPlansConfigSnapshot.exists
        ? existingFloorPlansConfigSnapshot.data() || {}
        : {};

      const existingFormFields = Array.isArray(existingFormConfig.formFields)
        ? existingFormConfig.formFields
        : currentSurveyData?.formFields || [];
      const existingFormOptions =
        existingFormConfig.formOptions ??
        currentSurveyData?.formOptions ??
        null;
      const nextFormOptions = formOptionsForWrite;
      const formConfigChanged =
        !isEqual(existingFormFields, formFieldsParsed) ||
        !isEqual(existingFormOptions, nextFormOptions);
      if (formConfigChanged) {
        surveyUpdateObj.formFieldsVersion =
          firebase.firestore.FieldValue.increment(1);
      }

      // only want to set up room schedule and floorplan if there actually is a room schedule
      if (roomSchedule) {
        const floorPlansForWrite = stripUnassignedFloorPlanMarkers(floorPlans);

        if (importData?.rows || !roomSchedule.fileName) {
          surveyUpdateObj.roomSchedule = {
            data: roomSchedule.data,
            floors: roomSchedule.floors || [],
          };
        } else {
          surveyUpdateObj.roomSchedule = {
            ...roomSchedule,
          };
        }

        surveyUpdateObj.floorPlans = [];

        const fpUploads = [];
        floorPlansForWrite?.forEach((floorPlan, index) => {
          const {
            fileName,
            lastModified,
            markers,
            width,
            height,
            url,
            data,
            floor,
          } = floorPlan;

          const existingPlan = currentSurveyData?.floorPlans?.find(
            (fp) => fp.floor === floor,
          );

          const sameFile =
            existingPlan && isSameFloorplan(existingPlan, floorPlan);

          surveyUpdateObj.floorPlans[index] =
            url || data
              ? {
                  fileName,
                  lastModified,
                  markers,
                  width,
                  height,
                  url: sameFile ? existingPlan.url : url,
                  floor,
                }
              : { floor };

          if (data && (!surveyId || !sameFile)) {
            const { name: fpName } = fileNameAndExtensionFromPath(fileName);
            const floorPlanPath = `${client.id}/${id}/${fpName}.jpg`;
            const floorPlanRef = floorPlanStorage.ref(floorPlanPath);
            const { fileUrl } = files[floor];
            fpUploads.push(
              uploadFloorplan(floorPlanRef, fileUrl, floor, fileName),
            );
          }
        });

        const completedUploads = await Promise.all(fpUploads);

        completedUploads.forEach(({ url, floor }) => {
          if (url) {
            const floorPlanIndex = surveyUpdateObj?.floorPlans?.findIndex(
              (fp) => fp.floor === floor,
            );

            if (floorPlanIndex >= 0) {
              surveyUpdateObj.floorPlans[floorPlanIndex] = {
                ...surveyUpdateObj.floorPlans[floorPlanIndex],
                url,
              };
            }
          }
        });
      }

      const { floorPlans: existingResolvedFloorPlans } =
        resolveSurveyFloorPlansConfig({
          surveyDocData: currentSurveyData,
          floorPlansConfigData: existingFloorPlansConfig,
          context: 'onSurveySubmitted',
          surveyId: id,
        });
      const existingFloorPlansByFloor = floorPlansArrayToConfigMap(
        existingResolvedFloorPlans || [],
      );
      const nextFloorPlansByFloor = floorPlansArrayToConfigMap(
        surveyUpdateObj.floorPlans || [],
      );
      const floorPlansChanged = !isEqual(
        existingFloorPlansByFloor,
        nextFloorPlansByFloor,
      );
      if (floorPlansChanged) {
        surveyUpdateObj.floorPlansVersion =
          firebase.firestore.FieldValue.increment(1);
      }

      if (
        surveyUpdateObj.roomSchedule?.data?.length ||
        (surveyId && currentSurveyData?.roomSchedule)
      ) {
        const roomsCollectionRef = surveyDocRef.collection('rooms');
        const roomScheduleData = surveyUpdateObj.roomSchedule?.data || [];
        const roomScheduleEntries = getRoomScheduleEntriesForSetup({
          roomScheduleData,
          excludeDeleted: true,
        });
        const deletedScheduleEntriesById =
          getDeletedScheduleEntriesById(roomScheduleData);
        const deletionCounterChanges = {
          rooms: 0,
          assets: 0,
          partials: 0,
        };
        const roomsLastUpdated = getLastUpdated().lastUpdated;
        const { existingRoomDocs, roomScheduleDataById } =
          await queueRoomDocSetupWrites({
            batch,
            roomsCollectionRef,
            roomScheduleEntries,
            surveyData: { ...surveyUpdateObj, id },
            lastUpdated: roomsLastUpdated,
          });

        roomScheduleEntries.forEach((roomEntry) => {
          if (!roomEntry.id) {
            return;
          }
          const matchingRoom = surveyUpdateObj.roomSchedule.data.find(
            (room) =>
              room?.id === roomEntry.id ||
              (!room?.id &&
                room?.name === roomEntry.name &&
                room?.floor === roomEntry.floor),
          );
          if (matchingRoom && !matchingRoom.id) {
            matchingRoom.id = roomEntry.id;
          }
        });

        for (const existingRoomDoc of existingRoomDocs.docs) {
          const roomId = existingRoomDoc.id;

          if (roomScheduleDataById.has(roomId)) {
            continue;
          }

          const existingRoomData = existingRoomDoc.data();
          const deletedAt =
            deletedScheduleEntriesById.get(roomId)?.deleted ||
            existingRoomData.deleted ||
            Date.now();
          const roomDeletionCounterChanges = await queueRoomAndContentsDeletion(
            {
              batch,
              roomDoc: existingRoomDoc,
              deletedAt,
              lastUpdated: roomsLastUpdated,
            },
          );

          deletionCounterChanges.rooms += roomDeletionCounterChanges.rooms;
          deletionCounterChanges.assets += roomDeletionCounterChanges.assets;
          deletionCounterChanges.partials +=
            roomDeletionCounterChanges.partials;
        }

        if (surveyId) {
          surveyUpdateObj.roomsNumDeleted =
            (surveyUpdateObj.roomsNumDeleted || 0) +
            deletionCounterChanges.rooms;
          surveyUpdateObj.assetsNumDeleted =
            (surveyUpdateObj.assetsNumDeleted || 0) +
            deletionCounterChanges.assets;
          surveyUpdateObj.partialsNumDeleted =
            (surveyUpdateObj.partialsNumDeleted || 0) +
            deletionCounterChanges.partials;
        }
      }

      checkForUndefinedInObject(surveyUpdateObj, 'surveyUpdateObj');

      const surveyWriteData = { ...surveyUpdateObj, ...getLastUpdated() };
      if (surveyId) {
        batch.update(surveyDocRef, surveyWriteData);
      } else {
        batch.set(surveyDocRef, surveyWriteData, { merge: true });
      }
      queueSurveyConfigDocWrites({
        batch,
        formConfigDocRef,
        floorPlansConfigDocRef,
        formFields: formFieldsParsed,
        formOptions: formOptionsForWrite,
        floorPlansByFloor: nextFloorPlansByFloor,
      });

      await batch.commit();

      if (!importData?.rows) {
        return { id, data: surveyUpdateObj };
      }

      const importsSortedByRoom = getImportsSortedByRoom(
        importData.rows,
        roomSchedule.data,
      );

      functions.httpsCallable('importAssetData')({
        importsSortedByRoom,
        clientId,
        surveyId: id,
        name,
        siteId,
        siteName,
        surveyorId,
        surveyorName,
        surveyorEmail,
        buildingId,
        buildingName,
        assetListId,
        formFields: formFieldsParsed,
        roomSchedule,
        assetsNumImported: currentSurveyData?.assetsNumImported || 0,
      });

      return { id, data: surveyUpdateObj };
    } catch (error) {
      const surveyDataWithoutBlob = {
        ...surveyForm,
        floorPlans: surveyForm.floorPlans?.map((fp) => ({
          ...fp,
          data: fp.data?.substr(0, 4) === 'data' ? 'Is data URL' : fp.data,
        })),
      };
      const filesDataWithoutBlob = Object.keys(files)?.map((fileId) => ({
        id: fileId,
        ...files[fileId],
        fileUrl:
          files[fileId].fileUrl.substr(0, 4) === 'data'
            ? 'Is data URL'
            : files[fileId].fileUrl,
      }));
      recordError(error, {
        surveyUpdateObj,
        surveyData: surveyDataWithoutBlob,
        files: filesDataWithoutBlob,
      });
      dispatch(setLoading(false));

      return { error };
    }
  };

export const onSelectedSurveyDelete =
  ({ survey }) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    const firestore = getFirestore();
    const { client } = getState().firestore.data;
    const deletedDate = Date.now();

    dispatch(setSnackMessage({ message: 'Survey deleted' }));

    const batch = firestore.batch();

    const clientDocRef = firestore.collection('clients').doc(client.id);
    const surveyDocRef = clientDocRef.collection('surveys').doc(survey.id);

    batch.update(surveyDocRef, {
      deleted: deletedDate,
      ...getLastUpdated(),
    });

    return batch.commit();
  };

export const onSelectedSurveyReissue =
  ({ survey, resetRooms, assignment }) =>
  async (dispatch, getState, { getFirebase, getFirestore }) => {
    const firestore = getFirestore();
    const { client } = getState().firestore.data;

    const batch = new BigBatch({ firestore });

    const clientDocRef = firestore.collection('clients').doc(client.id);
    const surveyDocRef = clientDocRef.collection('surveys').doc(survey.id);
    const { formConfigDocRef, floorPlansConfigDocRef } =
      getSurveyConfigDocRefs(surveyDocRef);
    const fallbackMainSurveyorId = survey?.surveyorId || '';
    const fallbackSelectedSurveyorIds = Array.isArray(survey?.surveyorIds)
      ? survey.surveyorIds
      : [fallbackMainSurveyorId];
    const assignmentPayload = {
      mainSurveyorId: assignment?.mainSurveyorId || fallbackMainSurveyorId,
      mainSurveyorName: assignment?.mainSurveyorName || survey?.surveyorName,
      mainSurveyorEmail: assignment?.mainSurveyorEmail || survey?.surveyorEmail,
      additionalSurveyorIds:
        assignment?.additionalSurveyorIds ||
        fallbackSelectedSurveyorIds.filter(
          (uid) => uid !== fallbackMainSurveyorId,
        ),
      selectedSurveyorsById: assignment?.selectedSurveyorsById || {},
    };
    const canonicalSurveyorAssignment = getCanonicalSurveyorAssignment({
      selectedSurveyorIds: [
        assignmentPayload.mainSurveyorId,
        ...(assignmentPayload.additionalSurveyorIds || []),
      ],
      mainSurveyorId: assignmentPayload.mainSurveyorId,
      mainSurveyorName: assignmentPayload.mainSurveyorName,
      mainSurveyorEmail: assignmentPayload.mainSurveyorEmail,
      existingSurveyorsMap: survey?.surveyors || {},
      surveyorsById: assignmentPayload.selectedSurveyorsById,
      preserveExistingStatus: false,
      defaultStatus: Statuses.ISSUED,
    });
    const additionalSurveyorCount = Math.max(
      (canonicalSurveyorAssignment.surveyorIds || []).length - 1,
      0,
    );
    const reissueStatus = getSurveyStatusForAssignment({
      mainSurveyorId: canonicalSurveyorAssignment.surveyorId,
      surveyorIds: canonicalSurveyorAssignment.surveyorIds,
    });

    dispatch(
      setSnackMessage({
        message: additionalSurveyorCount
          ? `${survey.name} reissued to ${canonicalSurveyorAssignment.surveyorName} (+${additionalSurveyorCount} additional)`
          : `${survey.name} reissued to ${canonicalSurveyorAssignment.surveyorName}`,
      }),
    );

    const roomsCollectionRef = surveyDocRef.collection('rooms');
    const activeRoomScheduleEntries = getRoomScheduleEntriesForSetup({
      roomScheduleData: survey?.roomSchedule?.data,
      excludeDeleted: true,
    });
    if (activeRoomScheduleEntries.length) {
      const roomsLastUpdated = getLastUpdated().lastUpdated;
      await queueRoomDocSetupWrites({
        batch,
        roomsCollectionRef,
        roomScheduleEntries: activeRoomScheduleEntries,
        surveyData: {
          ...survey,
          ...canonicalSurveyorAssignment,
          id: survey.id,
          status: reissueStatus,
        },
        lastUpdated: roomsLastUpdated,
      });
    }

    let roomSnapshot;
    if (resetRooms) {
      roomSnapshot = await roomsCollectionRef.where('deleted', '==', 0).get();
    }

    batch.update(surveyDocRef, {
      status: reissueStatus,
      ...canonicalSurveyorAssignment,
      ...getLegacyVersionMigrationPatch(survey),
      ...(resetRooms
        ? {
            roomsNumCompleted: 0,
            roomsNumInProgress: 0,
            roomsNumNotStarted:
              activeRoomScheduleEntries.length || roomSnapshot?.size || 0,
          }
        : {}),
      ...getLastUpdated(),
    });
    queueSurveyConfigDocWrites({
      batch,
      formConfigDocRef,
      floorPlansConfigDocRef,
      formFields: Array.isArray(survey?.formFields) ? survey.formFields : [],
      formOptions:
        survey?.formOptions && typeof survey.formOptions === 'object'
          ? survey.formOptions
          : {},
      floorPlansByFloor: floorPlansArrayToConfigMap(
        Array.isArray(survey?.floorPlans) ? survey.floorPlans : [],
      ),
    });

    if (resetRooms && roomSnapshot) {
      roomSnapshot.docs.forEach((roomDoc) => {
        batch.update(roomDoc.ref, {
          status: Statuses.NOT_STARTED,
          surveyorStatus: {},
          ...getLastUpdated(),
        });
      });
    }

    return batch.commit();
  };
